'use client';
// ChartHeader Component - Shows current probability and change
import { useMemo } from 'react';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { Box, Typography } from '@mui/material';

interface ChartHeaderProps {
  currentProbability: number;
  previousProbability?: number;
  timestamp?: string;
}

// Format timestamp: "Aug 5, 2025 5:30 am"
function formatTimestamp(dateString?: string) {
  if (!dateString) return '';
  const date = new Date(dateString);
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  const month = months[date.getMonth()];
  const day = date.getDate();
  const year = date.getFullYear();
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const ampm = hours >= 12 ? 'pm' : 'am';
  const displayHours = hours % 12 || 12;
  const displayMinutes = minutes.toString().padStart(2, '0');

  return `${month} ${day}, ${year} ${displayHours}:${displayMinutes} ${ampm}`;
}

export function ChartHeader({
  currentProbability,
  previousProbability,
  timestamp,
}: ChartHeaderProps) {
  const change = useMemo(() => {
    if (previousProbability === undefined) return null;
    const diff = currentProbability - previousProbability;
    return {
      value: Math.abs(diff * 100),
      isPositive: diff >= 0,
    };
  }, [currentProbability, previousProbability]);

  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Typography
            variant="h4"
            sx={{
              fontSize: { xs: '1.75rem', sm: '2rem' },
              fontWeight: 700,
              color: '#3b82f6',
              lineHeight: 1.2,
            }}
          >
            {(currentProbability * 100).toFixed(0)}% chance
          </Typography>
          {change && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.25,
                color: change.isPositive ? '#22c55e' : '#ef4444',
                mt: 0.5,
              }}
            >
              {change.isPositive ? (
                <ArrowUpwardIcon sx={{ fontSize: '0.875rem' }} />
              ) : (
                <ArrowDownwardIcon sx={{ fontSize: '0.875rem' }} />
              )}
              <Typography
                variant="body2"
                sx={{
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: change.isPositive ? '#22c55e' : '#ef4444',
                }}
              >
                {change.value.toFixed(0)}%
              </Typography>
            </Box>
          )}
        </Box>
        {timestamp && (
          <Typography
            variant="body2"
            sx={{
              fontSize: '0.75rem',
              color: 'text.secondary',
              fontWeight: 400,
            }}
          >
            {formatTimestamp(timestamp)}
          </Typography>
        )}
      </Box>
    </Box>
  );
}
